import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import productRadarLogo from '@/assets/product_radar_logo_png_blue.png';

interface PasswordResetProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function PasswordReset({ onSuccess, onCancel }: PasswordResetProps) {
  const { resetPassword, updatePassword, session } = useAuth();
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if we're in a password reset flow (recovery token in URL or session exists from recovery)
  useEffect(() => {
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.substring(1));
    const type = hashParams.get('type');

    if (type === 'recovery') {
      setStep('reset');
      
      const cleanedHash = new URLSearchParams(hash.substring(1));
      cleanedHash.delete('access_token');
      cleanedHash.delete('refresh_token');
      cleanedHash.delete('expires_in');
      cleanedHash.delete('expires_at');
      cleanedHash.delete('token_type');
      cleanedHash.delete('type');
      const remainingHash = cleanedHash.toString();
      const newUrl = window.location.pathname + window.location.search + (remainingHash ? `#${remainingHash}` : '');
      window.history.replaceState({}, document.title, newUrl);
    } else if (session) {
      setStep('reset');
    }
  }, [session]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const { error } = await resetPassword(email);
      
      if (error) {
        toast.error('Failed to send reset email', {
          description: error.message
        });
        return;
      }

      toast.success('Password reset email sent', {
        description: 'Check your email for instructions to reset your password'
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error requesting password reset:', err);
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      if (!session) {
        toast.error('Invalid or expired reset link', {
          description: 'Please request a new password reset email'
        });
        setStep('request');
        setLoading(false);
        return;
      }

      const { error } = await updatePassword(newPassword);
      
      if (error) {
        toast.error('Failed to update password', {
          description: error.message
        });
        return;
      }

      toast.success('Password updated successfully');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      toast.error('Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  // Request step -- inline form (parent provides page wrapper when used in auth.tsx)
  if (step === 'request') {
    return (
      <form onSubmit={handleRequestReset} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="reset-email" className="text-sm">Email</Label>
          <Input
            id="reset-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            className="h-11"
            autoFocus
          />
        </div>

        <Button type="submit" className="w-full h-11" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
            </>
          ) : (
            'Send Reset Link'
          )}
        </Button>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center justify-center w-full text-sm text-muted-foreground hover:text-foreground transition-colors pt-2"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
            Back to sign in
          </button>
        )}
      </form>
    );
  }

  // Reset step -- standalone full-page layout (accessed from email link)
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F2F0EF] p-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img src={productRadarLogo} alt="Product Radar" className="h-12" />
        </div>

        <h1 className="text-2xl font-semibold text-center mb-2">Set new password</h1>
        <p className="text-sm text-muted-foreground text-center mb-8">
          Enter your new password below
        </p>

        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="newPassword" className="text-sm">New Password</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                disabled={loading}
                className="h-11 pr-10"
                autoFocus
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Must be at least 6 characters</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword" className="text-sm">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                disabled={loading}
                className="h-11 pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button type="submit" className="w-full h-11" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...
              </>
            ) : (
              'Update Password'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
